import type { ReactNode } from "react";

// Cornice condivisa da 404 / errore / caricamento delle rotte pubbliche: stesso
// telaio "telex" del composer, cosi le pagine di servizio non sembrano estranee
// al feed. Nessun hook, quindi e importabile sia da server che da client.
type PublicStatusPanelProps = {
  eyebrow: string;
  icon: ReactNode;
  title: string;
  titleTag?: "h1" | "p";
  description: string;
  detail?: string | null;
  tone?: "neutral" | "alert";
  children?: ReactNode;
};

export const PUBLIC_STATUS_ACTION_CLASS =
  "inline-flex items-center gap-2 rounded-md border border-[var(--accent)]/50 bg-[var(--accent)]/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--ink)] transition hover:border-[var(--accent)] hover:bg-[var(--accent)]/20";

export const PUBLIC_STATUS_SECONDARY_ACTION_CLASS =
  "inline-flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface-2)]/80 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--muted)] transition hover:border-[var(--accent)] hover:text-[var(--ink)]";

export function PublicStatusPanel({
  eyebrow,
  icon,
  title,
  titleTag = "h1",
  description,
  detail = null,
  tone = "neutral",
  children,
}: PublicStatusPanelProps) {
  const isAlert = tone === "alert";
  const Title = titleTag;

  return (
    <main className="mx-auto flex min-h-[70vh] w-full max-w-3xl flex-col items-center justify-center px-4 py-14 sm:py-20">
      <div className="w-full overflow-hidden rounded-3xl border border-[var(--line)] bg-[linear-gradient(180deg,var(--surface)_0%,var(--surface-2)_45%,var(--surface)_100%)] shadow-[0_30px_60px_rgba(0,0,0,0.5)]">
        <div
          className={`flex items-center justify-between border-b border-[var(--line)] bg-black/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.28em] ${
            isAlert ? "text-rose-300" : "text-[var(--accent)]"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`inline-block h-2 w-2 animate-pulse rounded-full ${
                isAlert
                  ? "bg-rose-400 shadow-[0_0_10px_rgba(251,113,133,0.8)]"
                  : "bg-[var(--accent)] shadow-[0_0_10px_var(--accent)]"
              }`}
            />
            {eyebrow}
          </span>
          <span className="hidden sm:inline">silentpeople / station 01</span>
        </div>
        <div className="flex flex-col items-center gap-4 px-6 py-10 text-center sm:px-10 sm:py-14">
          <span
            aria-hidden="true"
            className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl border ${
              isAlert
                ? "border-rose-500/40 bg-rose-950/40 text-rose-300"
                : "border-[var(--line)] bg-[var(--surface-2)] text-[var(--accent)]"
            }`}
          >
            {icon}
          </span>
          <Title className="text-2xl font-semibold tracking-tight text-[var(--ink)] sm:text-3xl">
            {title}
          </Title>
          <p className="max-w-prose text-sm leading-relaxed text-[var(--ink-softer)] sm:text-base">
            {description}
          </p>
          {detail ? (
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
              {detail}
            </p>
          ) : null}
          {children ? (
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{children}</div>
          ) : null}
        </div>
      </div>
    </main>
  );
}
